import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Award, CheckCircle, XCircle, Trophy, Share2, Copy, ExternalLink,
    ArrowRight, ChevronDown, ChevronUp, Clock, Shield, AlertTriangle, Linkedin
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import confetti from 'canvas-confetti';

const QuizResultsPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { result, quizData, timeTaken } = location.state || {};

    const [showDetails, setShowDetails] = useState(false);
    const [copied, setCopied] = useState(false);

    // Fire confetti on pass
    useEffect(() => {
        if (result?.passed) {
            const duration = 2000;
            const end = Date.now() + duration;
            const colors = ['#a855f7', '#7c3aed', '#8b5cf6', '#c084fc'];

            const frame = () => {
                confetti({
                    particleCount: 3,
                    angle: 60,
                    spread: 55,
                    origin: { x: 0 },
                    colors,
                });
                confetti({
                    particleCount: 3,
                    angle: 120,
                    spread: 55,
                    origin: { x: 1 },
                    colors,
                });
                if (Date.now() < end) requestAnimationFrame(frame);
            };
            frame();
        }
    }, [result?.passed]);

    if (!result) {
        return (
            <div className="min-h-screen bg-[#08090c] flex items-center justify-center">
                <div className="text-center space-y-4">
                    <p className="text-slate-400">No results found.</p>
                    <button onClick={() => navigate('/premium-dashboard')} className="text-purple-400 underline text-sm">
                        Return to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    const passed = result.passed;
    const score = result.score;
    const credentialUrl = result.credential_url;
    const credentialId = result.credential_id;

    const formatTime = (s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m}m ${sec}s`;
    };

    const handleCopy = () => {
        if (credentialUrl) {
            navigator.clipboard.writeText(credentialUrl);
            setCopied(true);
            toast.success('Credential link copied!');
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const shareOnLinkedIn = () => {
        const text = `I just earned a verified ${quizData?.skill_area || 'skill'} credential from Rastor AI! 🎓\n\nScored ${score}% on the skill assessment.\n\nVerify: ${credentialUrl}`;
        const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(credentialUrl || 'https://rastorai.in')}&summary=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
    };

    return (
        <div className="min-h-screen bg-[#08090c] py-12 px-4">
            <div className="max-w-2xl mx-auto space-y-8">

                {/* Hero result */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-center space-y-6"
                >
                    {/* Icon */}
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
                        className="mx-auto"
                    >
                        {passed ? (
                            <div className="w-24 h-24 bg-gradient-to-br from-purple-500/20 to-violet-500/20 rounded-3xl flex items-center justify-center border border-purple-500/20 mx-auto">
                                <Trophy className="w-12 h-12 text-purple-400" />
                            </div>
                        ) : (
                            <div className="w-24 h-24 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-3xl flex items-center justify-center border border-amber-500/20 mx-auto">
                                <AlertTriangle className="w-12 h-12 text-amber-400" />
                            </div>
                        )}
                    </motion.div>

                    {/* Score */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="space-y-2"
                    >
                        <h1 className={`text-5xl font-black ${passed ? 'text-purple-400' : 'text-amber-400'}`}>
                            {score}%
                        </h1>
                        <p className="text-white text-xl font-bold">
                            {passed ? 'Credential Earned! 🎉' : 'Not Quite There Yet'}
                        </p>
                        <p className="text-slate-400 text-sm max-w-md mx-auto">
                            {passed
                                ? `Congratulations! You passed the ${quizData?.skill_area || ''} assessment and earned a verified credential.`
                                : `You need 70% to earn this credential. Review the material and try again in 24 hours.`
                            }
                        </p>
                    </motion.div>

                    {/* Stats row */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="flex justify-center gap-6"
                    >
                        <div className="text-center">
                            <p className="text-lg font-bold text-white">{result.correct}/{result.total}</p>
                            <p className="text-[10px] font-mono text-slate-500 uppercase">Correct</p>
                        </div>
                        <div className="w-px bg-white/10" />
                        <div className="text-center">
                            <p className="text-lg font-bold text-white">{formatTime(timeTaken || result.time_taken_seconds || 0)}</p>
                            <p className="text-[10px] font-mono text-slate-500 uppercase">Time Taken</p>
                        </div>
                        <div className="w-px bg-white/10" />
                        <div className="text-center">
                            <p className="text-lg font-bold text-white">{result.integrity_flags || 0}</p>
                            <p className="text-[10px] font-mono text-slate-500 uppercase">Integrity Flags</p>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Credential card (pass only) */}
                {passed && credentialId && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 }}
                        className="bg-gradient-to-br from-[#1a1040] via-[#0F131F] to-[#0a1628] border border-purple-500/20 rounded-2xl p-6 space-y-4"
                    >
                        {/* Certificate preview */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-purple-500/10 rounded-xl flex items-center justify-center">
                                    <Award className="w-5 h-5 text-purple-400" />
                                </div>
                                <div>
                                    <p className="text-white font-bold text-sm">{quizData?.skill_area || 'Skill Credential'}</p>
                                    <p className="text-slate-500 text-[11px] font-mono">{quizData?.career_path}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 bg-emerald-500/10 px-2.5 py-1 rounded-full">
                                <Shield className="w-3 h-3 text-emerald-400" />
                                <span className="text-[10px] font-mono text-emerald-400 uppercase">Verified</span>
                            </div>
                        </div>

                        <div className="border-t border-white/5 pt-4 space-y-3">
                            {/* Credential URL */}
                            <div>
                                <label className="text-[10px] font-mono text-slate-500 uppercase block mb-1">Verification Link</label>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 bg-[#0B0F19] border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-slate-300 truncate">
                                        {credentialUrl}
                                    </div>
                                    <button
                                        onClick={handleCopy}
                                        className="px-2.5 py-2 bg-purple-500/10 border border-purple-500/20 rounded-lg text-purple-400 hover:bg-purple-500/20 transition-colors"
                                    >
                                        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            {/* Share buttons */}
                            <div className="flex gap-2">
                                <button
                                    onClick={shareOnLinkedIn}
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#0077B5]/10 border border-[#0077B5]/20 rounded-lg text-[#0077B5] text-xs font-medium hover:bg-[#0077B5]/20 transition-colors"
                                >
                                    <Linkedin className="w-3.5 h-3.5" />
                                    Share on LinkedIn
                                </button>
                                <a
                                    href={`/verify/${credentialId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-slate-300 text-xs hover:bg-white/10 transition-colors"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    View Public Page
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Question breakdown */}
                <div className="bg-[#0F131F] border border-white/5 rounded-2xl overflow-hidden">
                    <button
                        onClick={() => setShowDetails(!showDetails)}
                        className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/[0.02] transition-colors"
                    >
                        <span className="text-sm text-white font-medium">Question Breakdown</span>
                        {showDetails ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                    </button>

                    {showDetails && (
                        <div className="border-t border-white/5 divide-y divide-white/5">
                            {result.results?.map((r, i) => (
                                <div key={r.question_id} className="px-6 py-4 space-y-2">
                                    <div className="flex items-start gap-3">
                                        {r.correct ? (
                                            <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                                        ) : (
                                            <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                                        )}
                                        <div className="space-y-1 flex-1">
                                            <p className="text-sm text-white">Q{i + 1}: {quizData?.questions?.[i]?.question || `Question ${i + 1}`}</p>
                                            {!r.correct && (
                                                <p className="text-xs text-slate-500">
                                                    Your answer: <span className="text-red-400">{r.user_answer || '—'}</span> ·
                                                    Correct: <span className="text-emerald-400">{r.correct_answer}</span>
                                                </p>
                                            )}
                                            {r.explanation && (
                                                <p className="text-xs text-slate-500 italic">{r.explanation}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-center">
                    <button
                        onClick={() => navigate('/premium-dashboard')}
                        className="px-5 py-2.5 bg-white/5 border border-white/10 rounded-lg text-slate-300 text-sm hover:bg-white/10 transition-colors"
                    >
                        Back to Dashboard
                    </button>
                    {!passed && (
                        <button
                            onClick={() => navigate(-2)} // Go back to roadmap
                            className="flex items-center gap-2 px-5 py-2.5 bg-purple-500/10 border border-purple-500/20 rounded-lg text-purple-400 text-sm hover:bg-purple-500/20 transition-colors"
                        >
                            Review Material
                            <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuizResultsPage;
